import { apiClient } from './client';
import {
  ContextEvent,
  ContextState,
  AdaptationRule,
  PrivacySettings,
  ActivityAnalysis,
  Prediction,
  Adaptation,
  MLModel,
} from '../../types/context';

export const contextAPI = {
  // Context Events
  collectEvent: (event: Omit<ContextEvent, 'id' | 'timestamp'>) =>
    apiClient.post<{ eventId: string }>('/api/v1/context/events', event),

  getEvents: (params?: {
    source?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get<{ events: ContextEvent[]; total: number }>('/api/v1/context/events', {
      params,
    }),

  // Context State
  getCurrentState: () =>
    apiClient.get<{ state: ContextState }>('/api/v1/context/state'),

  getStateHistory: (params: { period: string }) =>
    apiClient.get<{ states: ContextState[] }>('/api/v1/context/state/history', {
      params,
    }),

  // Adaptation Rules
  getAdaptationRules: () =>
    apiClient.get<{ rules: AdaptationRule[] }>('/api/v1/adaptations/rules'),

  getAdaptationRule: (ruleId: string) =>
    apiClient.get<{ rule: AdaptationRule }>(`/api/v1/adaptations/rules/${ruleId}`),

  createAdaptationRule: (rule: Omit<AdaptationRule, 'id' | 'created_at' | 'updated_at'>) =>
    apiClient.post<{ rule: AdaptationRule }>('/api/v1/adaptations/rules', rule),

  updateAdaptationRule: (ruleId: string, updates: Partial<AdaptationRule>) =>
    apiClient.put<{ rule: AdaptationRule }>(`/api/v1/adaptations/rules/${ruleId}`, updates),

  deleteAdaptationRule: (ruleId: string) =>
    apiClient.delete(`/api/v1/adaptations/rules/${ruleId}`),

  testAdaptationRule: (rule: AdaptationRule) =>
    apiClient.post<{ result: any }>('/api/v1/adaptations/rules/test', rule),

  // Current Adaptations
  getCurrentAdaptations: () =>
    apiClient.get<{ adaptations: Adaptation[] }>('/api/v1/adaptations/current'),

  // Privacy Settings
  getPrivacySettings: () =>
    apiClient.get<{ settings: PrivacySettings }>('/api/v1/privacy/settings'),

  updatePrivacySettings: (settings: Partial<PrivacySettings>) =>
    apiClient.put<{ settings: PrivacySettings }>('/api/v1/privacy/settings', settings),

  exportPrivacyData: () =>
    apiClient.post<{ exportId: string }>('/api/v1/privacy/export'),

  deletePrivacyData: (params: { before?: string; sources?: string[] }) =>
    apiClient.delete('/api/v1/privacy/data', { params }),

  // Activity Analysis
  getActivityAnalysis: (params: { period: string }) =>
    apiClient.get<ActivityAnalysis>('/api/v1/analysis/activity', { params }),

  getFocusAnalysis: (params: { period: string }) =>
    apiClient.get<any>('/api/v1/analysis/focus', { params }),

  getProductivityAnalysis: (params: { period: string }) =>
    apiClient.get<any>('/api/v1/analysis/productivity', { params }),

  getPatterns: () =>
    apiClient.get<{ patterns: any[] }>('/api/v1/analysis/patterns'),

  // Predictions
  getNextActionPrediction: () =>
    apiClient.get<{ predictions: Prediction[] }>('/api/v1/predictions/next-action'),

  getIntentionPrediction: () =>
    apiClient.get<{ predictions: Prediction[] }>('/api/v1/predictions/intention'),

  getFocusPrediction: () =>
    apiClient.get<{ prediction: any }>('/api/v1/predictions/focus'),

  getInterruptionRisk: () =>
    apiClient.get<{ risk: any }>('/api/v1/predictions/interruption-risk'),

  // ML Models
  getMLModels: () =>
    apiClient.get<{ models: MLModel[] }>('/api/v1/context/ml/models'),

  getMLModelStatus: (modelId: string) =>
    apiClient.get<{ model: MLModel }>(`/api/v1/context/ml/models/${modelId}`),

  trainMLModel: (modelId: string) =>
    apiClient.post<{ trainingId: string }>(`/api/v1/context/ml/models/${modelId}/train`),

  // Knowledge Integration
  getKnowledgeEnrichments: (params?: { nodeId?: string }) =>
    apiClient.get<{ enrichments: any[] }>('/api/v1/knowledge/enrichments', { params }),

  getSuggestedConnections: () =>
    apiClient.get<{ connections: any[] }>('/api/v1/knowledge/suggested-connections'),

  getContextualSearch: (query: string) =>
    apiClient.post<{ results: any[] }>('/api/v1/knowledge/contextual-search', { query }),
};